import { useQuery, useQueryClient } from '@tanstack/react-query';
import { type Profile, profileApi } from '@/api/admin/profiles';
import { Combobox } from '@/components/ui/combobox';

interface ProfileSelectorProps {
  profileId: string;
  onProfileChange: (profileId: string) => void;
}

export function ProfileSelector({ profileId, onProfileChange }: ProfileSelectorProps) {
  const queryClient = useQueryClient();
  const { data: profilesData, isLoading: isLoadingProfiles } = useQuery({
    queryKey: ['profiles', { limit: 100 }],
    queryFn: () => profileApi.findMany({ limit: 100 }),
  });

  const { data: selectedProfile } = useQuery({
    queryKey: ['profile', profileId],
    queryFn: () => profileApi.findOne(profileId),
    enabled: !!profileId,
  });

  const currentProfile =
    selectedProfile?.id === profileId
      ? selectedProfile
      : profilesData?.docs.find((profile) => profile.id === profileId);

  const handleProfileChange = (profiles: Profile[]) => {
    if (profiles.length > 0) {
      // The first selection mounts a new selector inside the detail view.
      // Preserve the chosen option before navigation starts its profile query.
      queryClient.setQueryData<Profile>(
        ['profile', profiles[0].id],
        (cached) => cached ?? profiles[0],
      );
      onProfileChange(profiles[0].id);
    } else {
      onProfileChange('');
    }
  };

  return (
    <div className="sm:w-96">
      <label htmlFor="profile" className="block text-sm/6 font-medium text-gray-900">
        Perfil
      </label>
      <div className="mt-2">
        <Combobox
          id="profile"
          data={profilesData?.docs ?? []}
          displayValueGetter={(doc) => doc.name}
          keyExtractor={(doc) => doc.id}
          onOptionChange={handleProfileChange}
          type="single"
          value={currentProfile ? [currentProfile] : []}
          placeholder="Escolha uma opção"
          isLoading={isLoadingProfiles}
        />
      </div>
    </div>
  );
}
